// src/services/vocabulaire.service.ts
//
// Vocabulaire métier de l'organisation connectée.
//
// Les écrans Fédération ont été écrits pour une fédération sportive : ligues,
// clubs, joueurs. La même mécanique sert à une fédération d'auto-écoles, dont
// les affiliés sont des établissements et les licenciés des moniteurs et des
// candidats. Les mots viennent donc du backend (GET /vocabulaire-metier), avec
// le jeu sportif livré ici en dur comme repli : si l'appel échoue, ou si
// l'organisation n'a rien configuré, l'écran affiche EXACTEMENT ce qu'il
// affichait avant — c'est ce qui garantit que la FFG ne voit aucun changement.

import { useEffect, useState } from 'react';
import axios from 'axios';
import axiosInstance from './axios.config';
import { API_URL } from '../config/env';

export interface TermeMetier { s: string; p: string; genre: 'M' | 'F' }
export interface TypeAdherentMetier { code: string; libelle: string; personneMorale: boolean }
/**
 * Carte du parcours « Adhérer ». `type` reste l'identifiant TECHNIQUE
 * (ENTREPRISE, FEDERATION, EPA) : il est soumis au backend, conditionne le
 * formulaire qui suit et le type de membre créé. Seuls les textes changent.
 */
export interface CarteAdhesionPublique {
  type: 'ENTREPRISE' | 'FEDERATION' | 'EPA';
  title: string; subtitle: string; description: string;
  icone: string; examples: string[]; color: string;
}
/**
 * Un champ du formulaire d'adhésion configuré.
 *
 * `chemin` est la place de la valeur dans le JSONB `donnees` soumis au
 * backend, en notation pointée. Le libellé n'est qu'un libellé : le
 * renommer ne déplace aucune donnée.
 */
export interface ChampAdhesionConfig {
  chemin: string;
  libelle: string;
  type: 'text' | 'email' | 'tel' | 'url' | 'number' | 'date' | 'montant'
    | 'textarea' | 'select' | 'multiselect' | 'radio' | 'fichier';
  obligatoire?: boolean;
  majuscules?: boolean;
  placeholder?: string;
  aide?: string;
  lignes?: number;
  pleineLargeur?: boolean;
  options?: Array<{ value: string; label: string }>;
  nomenclature?: string;
  /** Suffixe le libellé par la devise de l'organisation : jamais en dur. */
  suffixeDevise?: boolean;
  afficherSi?: { chemin: string; valeurs: string[] };
  verifierUnicite?: 'nif';
}
export interface SectionAdhesionConfig {
  titre: string;
  description?: string;
  colonnes?: 1 | 2 | 3;
  champs: ChampAdhesionConfig[];
}
export interface EtapeAdhesionConfig {
  libelle: string;
  description?: string;
  titre?: string;
  sections?: SectionAdhesionConfig[];
  pieces?: boolean;
}
export interface FormulaireAdhesionConfig {
  titre?: string;
  etapes: EtapeAdhesionConfig[];
  pieces: Array<{ libelle: string; obligatoire?: boolean }>;
  lieuSignatureDefaut?: string;
  pays?: string;
  mappingPlat?: Record<string, string[]>;
}

export interface VocabulaireResolu {
  code: string;
  libelleJeu: string;
  moduleFederation: { libelle: string; description: string };
  moduleAdherents: { libelle: string; description: string };
  termes: Record<string, TermeMetier>;
  typesAdherent: TypeAdherentMetier[];
  cartesAdhesion: CarteAdhesionPublique[];
  /**
   * Formulaires du parcours public, par type technique. VIDE pour une
   * organisation sans configuration : les formulaires codés d'origine sont
   * alors rendus tels quels. C'est le mécanisme de non-régression.
   */
  formulairesAdhesion: Record<string, FormulaireAdhesionConfig>;
  nomenclatures: Record<string, string[]>;
  /** Devise ISO de l'organisation (`ecom_entities.devise`). Jamais en dur. */
  devise: string | null;
  parDefaut: boolean;
}

/** Repli : les mots livrés d'origine. Toute clé manquante retombe ici. */
export const VOCABULAIRE_SPORT: VocabulaireResolu = {
  code: 'SPORT',
  libelleJeu: 'Fédération sportive (socle)',
  moduleFederation: { libelle: 'Fédération sportive', description: '' },
  moduleAdherents: { libelle: 'Gestion des Adhérents', description: '' },
  termes: {
    federation: { s: 'Fédération', p: 'Fédérations', genre: 'F' },
    ligue: { s: 'Ligue', p: 'Ligues', genre: 'F' },
    club: { s: 'Club', p: 'Clubs', genre: 'M' },
    licencie: { s: 'Licencié', p: 'Licenciés', genre: 'M' },
    licenciePrincipal: { s: 'Joueur', p: 'Joueurs', genre: 'M' },
    licencieSecondaire: { s: 'Dirigeant', p: 'Dirigeants', genre: 'M' },
    affiliation: { s: 'Affiliation', p: 'Affiliations', genre: 'F' },
    agrement: { s: 'Agrément', p: 'Agréments', genre: 'M' },
    licence: { s: 'Licence', p: 'Licences', genre: 'F' },
    saison: { s: 'Saison', p: 'Saisons', genre: 'F' },
    commission: { s: 'Commission', p: 'Commissions', genre: 'F' },
  },
  typesAdherent: [
    { code: 'ENTREPRISE', libelle: 'Entreprise', personneMorale: true },
    { code: 'FEDERATION', libelle: 'Fédération / organisation patronale', personneMorale: true },
    { code: 'EPA', libelle: 'Établissement public', personneMorale: true },
    { code: 'AUTRE', libelle: 'Autre', personneMorale: true },
  ],
  // Recopie des trois cartes historiques de TypeSelector.tsx : c'est le repli
  // qui garantit que la CGE-GUI voit sa page inchangée.
  cartesAdhesion: [
    {
      type: 'ENTREPRISE', title: 'Entreprise', subtitle: 'Société commerciale ou industrielle',
      description: "Vous représentez une société (SA, SARL, SAS, EI…) qui souhaite adhérer à titre individuel. Vous bénéficierez de l'accompagnement, des formations et du réseau d'affaires.",
      icone: 'Building2', examples: ['SA', 'SARL', 'SAS', 'GIE', 'EI'], color: '#1e40af',
    },
    {
      type: 'FEDERATION', title: 'Fédération patronale', subtitle: 'Regroupement sectoriel',
      description: "Vous représentez une fédération patronale ou un regroupement d'entreprises d'un même secteur. Vous fédérez d'autres adhérents et participez à la gouvernance.",
      icone: 'Users', examples: ['Fédération industrie', 'Union sectorielle', 'Groupement professionnel'], color: '#7c3aed',
    },
    {
      type: 'EPA', title: 'Entreprises mixtes / Associations', subtitle: 'EPA, ordres, chambres, associations',
      description: 'Vous représentez un établissement public à caractère administratif, un ordre professionnel, une chambre, ou une association professionnelle reconnue.',
      icone: 'Landmark', examples: ['EPA', 'Chambre de commerce', 'Ordre professionnel', 'Association patronale'], color: '#0d9488',
    },
  ],
  // Vide à dessein : aucun formulaire configuré ⇒ EntrepriseForm,
  // FederationForm et EpaForm sont rendus tels qu'écrits.
  formulairesAdhesion: {},
  nomenclatures: {},
  devise: null,
  parDefaut: true,
};

let courant: VocabulaireResolu = VOCABULAIRE_SPORT;
let enCours: Promise<VocabulaireResolu> | null = null;
const abonnes = new Set<(v: VocabulaireResolu) => void>();

/** Vocabulaire déjà connu, sans attendre : jamais nul, jamais bloquant. */
export function vocabulaireCourant(): VocabulaireResolu { return courant; }

export async function chargerVocabulaire(force = false): Promise<VocabulaireResolu> {
  if (!force && enCours) return enCours;
  enCours = axiosInstance.get('/vocabulaire-metier')
    .then((r) => {
      const v = r.data as VocabulaireResolu;
      courant = {
        ...VOCABULAIRE_SPORT, ...v,
        termes: { ...VOCABULAIRE_SPORT.termes, ...(v?.termes ?? {}) },
        typesAdherent: v?.typesAdherent?.length ? v.typesAdherent : VOCABULAIRE_SPORT.typesAdherent,
      };
      abonnes.forEach((f) => f(courant));
      return courant;
    })
    .catch(() => courant);
  return enCours;
}

/** Réinitialise le cache (changement d'organisation, déconnexion). */
export function reinitialiserVocabulaire(): void {
  courant = VOCABULAIRE_SPORT;
  enCours = null;
  publicParSlug.clear();
  abonnes.forEach((f) => f(courant));
}

export interface Mots {
  v: VocabulaireResolu;
  /** Singulier. */
  mot: (cle: string) => string;
  /** Pluriel. */
  mots: (cle: string) => string;
  /** Singulier en minuscule, pour l'insérer dans une phrase. */
  min: (cle: string) => string;
  /** Pluriel en minuscule. */
  minP: (cle: string) => string;
  /** Accord : « le » / « la », « du » / « de la », « au » / « à la ». */
  accord: (cle: string, forme?: 'le' | 'du' | 'au' | 'e') => string;
  /**
   * Terme précédé de son article, élision comprise : « la saison »,
   * « l’année », « le club », « l’auto-école ». Sans cela, un vocabulaire
   * féminin commençant par une voyelle donne « la année ».
   */
  leMot: (cle: string, forme?: 'le' | 'du' | 'au') => string;
  /**
   * Adapte un libellé déjà rédigé en vocabulaire sportif (« Renvoyer au
   * club »). Pour une organisation sans jeu configuré — donc pour la FFG —
   * la fonction rend la chaîne IDENTIQUE, sans la parcourir. Elle ne sert
   * qu'aux libellés constants partagés, le reste des écrans appelle `mot`.
   */
  adapter: (texte: string) => string;
}

export function motsDe(v: VocabulaireResolu): Mots {
  const t = (cle: string) => v.termes[cle] ?? VOCABULAIRE_SPORT.termes[cle] ?? { s: cle, p: cle, genre: 'M' as const };
  return {
    v,
    mot: (c) => t(c).s,
    mots: (c) => t(c).p,
    min: (c) => t(c).s.toLocaleLowerCase('fr-FR'),
    minP: (c) => t(c).p.toLocaleLowerCase('fr-FR'),
    accord: (c, forme = 'le') => {
      const f = t(c).genre === 'F';
      if (forme === 'du') return f ? 'de la' : 'du';
      if (forme === 'au') return f ? 'à la' : 'au';
      if (forme === 'e') return f ? 'e' : '';
      return f ? 'la' : 'le';
    },
    leMot: (c, forme = 'le') => {
      const terme = t(c);
      const m = terme.s.toLocaleLowerCase('fr-FR');
      const voyelle = /^[aàâeéèêëiîïoôuùûüyh]/.test(m);
      const f = terme.genre === 'F';
      if (voyelle) return forme === 'du' ? `de l’${m}` : forme === 'au' ? `à l’${m}` : `l’${m}`;
      if (forme === 'du') return `${f ? 'de la' : 'du'} ${m}`;
      if (forme === 'au') return `${f ? 'à la' : 'au'} ${m}`;
      return `${f ? 'la' : 'le'} ${m}`;
    },
    adapter: (texte) => {
      if (v.parDefaut || !texte) return texte;
      let out = texte;
      for (const cle of Object.keys(VOCABULAIRE_SPORT.termes)) {
        const src = VOCABULAIRE_SPORT.termes[cle];
        const cible = t(cle);
        if (cible.s === src.s) continue;
        out = out
          .replace(new RegExp(`\\b${src.p}\\b`, 'g'), cible.p)
          .replace(new RegExp(`\\b${src.p.toLocaleLowerCase('fr-FR')}\\b`, 'g'), cible.p.toLocaleLowerCase('fr-FR'))
          .replace(new RegExp(`\\b${src.s}\\b`, 'g'), cible.s)
          .replace(new RegExp(`\\b${src.s.toLocaleLowerCase('fr-FR')}\\b`, 'g'), cible.s.toLocaleLowerCase('fr-FR'));
      }
      return out;
    },
  };
}

/**
 * Hook : rend d'abord le vocabulaire connu (donc les mots d'origine au
 * premier rendu), puis celui de l'organisation dès qu'il est arrivé.
 */
export function useVocabulaire(): Mots {
  const [v, setV] = useState<VocabulaireResolu>(courant);
  useEffect(() => {
    const maj = (x: VocabulaireResolu) => setV(x);
    abonnes.add(maj);
    chargerVocabulaire().then(maj).catch(() => undefined);
    return () => { abonnes.delete(maj); };
  }, []);
  return motsDe(v);
}

/**
 * Lecture PUBLIQUE, par slug de portail : le parcours « Adhérer » de la
 * vitrine n'a pas de jeton. Le repli reste le jeu d'origine, donc une vitrine
 * sans configuration — la CGE-GUI — affiche exactement ses cartes actuelles.
 */
/**
 * Cache par slug des lectures publiques.
 *
 * Il n'est pas là pour économiser une requête, mais pour supprimer une
 * attente visible : l'écran de choix du type lit déjà le vocabulaire (via
 * `useCartesAdhesion`), donc la réponse est en main AVANT que le visiteur
 * ne clique sur une carte. Sans ce cache, le formulaire suivant repartirait
 * en lecture et afficherait un bref indicateur de chargement là où
 * l'existant n'en montre aucun.
 */
const publicParSlug = new Map<string, Promise<VocabulaireResolu>>();

export async function chargerVocabulairePublic(slug: string): Promise<VocabulaireResolu> {
  const enCache = publicParSlug.get(slug);
  if (enCache) return enCache;
  const p = lirePublic(slug);
  publicParSlug.set(slug, p);
  return p;
}

async function lirePublic(slug: string): Promise<VocabulaireResolu> {
  try {
    const r = await axios.get(`${API_URL}/public/vocabulaire-metier/${encodeURIComponent(slug)}`);
    const v = r.data as Partial<VocabulaireResolu>;
    return {
      ...VOCABULAIRE_SPORT, ...v,
      termes: { ...VOCABULAIRE_SPORT.termes, ...(v?.termes ?? {}) },
      cartesAdhesion: v?.cartesAdhesion?.length ? v.cartesAdhesion : VOCABULAIRE_SPORT.cartesAdhesion,
      formulairesAdhesion: v?.formulairesAdhesion ?? {},
      nomenclatures: v?.nomenclatures ?? {},
      devise: v?.devise ?? null,
    } as VocabulaireResolu;
  } catch {
    return VOCABULAIRE_SPORT;
  }
}

/** Ce que le parcours public a besoin de savoir pour un type d'adhérent. */
export interface ConfigFormulaireAdhesion {
  /** Nul tant que la lecture n'a pas abouti, ou si rien n'est configuré. */
  formulaire: FormulaireAdhesionConfig | null;
  nomenclatures: Record<string, string[]>;
  devise: string | null;
  /**
   * Vrai dès que l'organisation configure AU MOINS un formulaire, quel que
   * soit le type demandé. Sert aux écrans communs du parcours — l'écran de
   * choix du type porte un barème de cotisation libellé en francs guinéens,
   * qui n'a de sens que pour l'organisation qui l'a fait écrire.
   */
  aDesFormulaires: boolean;
  /** Faux pendant la lecture : l'écran attend plutôt que de clignoter. */
  charge: boolean;
}

/**
 * Formulaire configuré pour un type d'adhérent.
 *
 * Renvoie `formulaire: null` quand l'organisation n'a rien configuré — et
 * c'est le cas de la CGE-GUI. L'appelant rend alors son formulaire codé,
 * strictement inchangé.
 *
 * `charge` existe pour une raison précise : si l'on rendait le formulaire
 * codé avant d'avoir la réponse, une auto-école verrait d'abord l'écran de
 * la confédération patronale, puis le sien. On attend donc la réponse, qui
 * est un seul appel sans jeton.
 */
export function useFormulaireAdhesion(
  type: string | null | undefined,
  slug?: string,
): ConfigFormulaireAdhesion {
  const VIDE: ConfigFormulaireAdhesion = {
    formulaire: null, nomenclatures: {}, devise: null, aDesFormulaires: false, charge: true,
  };
  const [etat, setEtat] = useState<ConfigFormulaireAdhesion>({ ...VIDE, charge: false });
  useEffect(() => {
    let vivant = true;
    setEtat((e) => ({ ...e, charge: false }));
    const lecture = slug ? chargerVocabulairePublic(slug) : chargerVocabulaire();
    lecture
      .then((v) => {
        if (!vivant) return;
        const tous = v?.formulairesAdhesion ?? {};
        const f = type ? (tous[String(type).toUpperCase()] ?? null) : null;
        setEtat({
          // Un formulaire sans étape ne vaut pas mieux que pas de formulaire.
          formulaire: f && Array.isArray(f.etapes) && f.etapes.length ? f : null,
          nomenclatures: v?.nomenclatures ?? {},
          devise: v?.devise ?? null,
          aDesFormulaires: Object.keys(tous).length > 0,
          charge: true,
        });
      })
      // Référentiel injoignable ⇒ formulaire codé : le parcours reste ouvert.
      .catch(() => { if (vivant) setEtat(VIDE); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, slug]);
  return etat;
}

/**
 * Cartes du parcours d'adhésion. Avec un slug : lecture publique. Sans :
 * l'organisation connectée (back-office). Dans les deux cas, le premier
 * rendu montre les cartes d'origine, donc aucun clignotement pour l'existant.
 */
export function useCartesAdhesion(slug?: string): CarteAdhesionPublique[] {
  const [cartes, setCartes] = useState<CarteAdhesionPublique[]>(courant.cartesAdhesion);
  useEffect(() => {
    let vivant = true;
    const prendre = (v: VocabulaireResolu) => { if (vivant && v?.cartesAdhesion?.length) setCartes(v.cartesAdhesion); };
    (slug ? chargerVocabulairePublic(slug) : chargerVocabulaire()).then(prendre).catch(() => undefined);
    return () => { vivant = false; };
  }, [slug]);
  return cartes;
}

export const vocabulaireService = {
  mien: () => axiosInstance.get('/vocabulaire-metier').then((r) => r.data as VocabulaireResolu),
  jeux: () => axiosInstance.get('/vocabulaire-metier/jeux').then((r) => r.data as Array<{ code: string; libelle: string; description: string; livreAvecProduit: boolean }>),
  affecter: (vocabulaireCode: string, surcharge: any = {}) =>
    axiosInstance.put('/vocabulaire-metier', { vocabulaireCode, surcharge }).then((r) => {
      reinitialiserVocabulaire();
      return chargerVocabulaire(true).then(() => r.data as VocabulaireResolu);
    }),
  retirer: () => axiosInstance.delete('/vocabulaire-metier').then((r) => {
    reinitialiserVocabulaire();
    return chargerVocabulaire(true).then(() => r.data as VocabulaireResolu);
  }),
};
